import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useFirestore } from '../../contexts/FirestoreContext';
import { collection, query, where, orderBy, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { Empty, Button, Tooltip } from 'antd';
import { ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import jsPDF from 'jspdf';
require('jspdf-autotable');

const IncidentLogTab = ({ incidentId }) => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [userOrgId, setUserOrgId] = useState(null);
  const [userNames, setUserNames] = useState({});

  const logTypes = [
    { 
      id: 'all', 
      name: 'All Activities',
      tooltip: 'Show all incident activities and events including staff changes, task updates, milestone changes, and regulatory triggers.'
    },
    { 
      id: 'task_created', 
      name: 'Tasks',
      tooltip: 'Task creation, updates, deletions, status changes, and assignments.'
    },
    { 
      id: 'milestone_created', 
      name: 'Milestones',
      tooltip: 'Milestone creation, updates, deletions, status changes, and timeline movements.'
    },
    { 
      id: 'regulatory_triggers_updated', 
      name: 'Regulatory',
      tooltip: 'Regulatory trigger updates and automatic notification task creation.'
    },
    { 
      id: 'staff_status_changed', 
      name: 'Staff Status',
      tooltip: 'Staff safety status changes including safe check-ins, hospitalised/deceased updates, manual entries, and SMS replies.'
    }
  ];

  useEffect(() => {
    // Get user's orgId
    const fetchUserOrgId = async () => {
      try {
        if (!currentUser?.uid) {
          console.error('No current user found');
          return;
        }
        const userDocRef = doc(db, 'c3_users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setUserOrgId(userDoc.data().orgId);
        } else {
          console.error('User document not found');
          setError('User data not found');
        }
      } catch (error) {
        console.error('Error fetching user orgId:', error);
        setError('Failed to load user data');
      }
    };
    fetchUserOrgId();
  }, [currentUser?.uid, db]);

  useEffect(() => {
    if (!incidentId || !userOrgId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // FIXED: Use c3_incident_logs instead of c3_auditLogs
    const logsRef = collection(db, 'c3_incident_logs');
    const q = query(
      logsRef,
      where('incidentId', '==', incidentId),
      where('orgId', '==', userOrgId),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(q, 
      async (querySnapshot) => {
        console.log(`📋 Incident Log: Processing ${querySnapshot.docs.length} log entries`);
        
        const logsList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Debug: Log the raw log data
        console.log('📋 Raw log data - User IDs found:', logsList.map(log => ({
          id: log.id,
          userId: log.userId,
          userFullName: log.userFullName,
          action: log.action
        })).slice(0, 3));

        // Fetch user full names for all unique userIds in the logs (EXCLUDE SYSTEM USERS)
        const uniqueUserIds = [...new Set(logsList.map(log => log.userId || log.authorId).filter(Boolean))];
        console.log(`👥 Incident Log: Found ${uniqueUserIds.length} unique user IDs:`, uniqueUserIds);
        
        // FILTER OUT system users to prevent permissions errors that BLOCK video call logging
        const realUserIds = uniqueUserIds.filter(userId => 
          userId !== 'System' && 
          userId !== 'system' && 
          userId !== 'N/A' &&
          !userId.includes('system@')
        );
        console.log(`👥 Real user IDs to fetch (excluding system): ${realUserIds.length}`, realUserIds);
        
        // Fetch full names for REAL users only (bypass cache to ensure fresh data)
        const userNamePromises = realUserIds.map(async (userId) => {
          const fullName = await getUserFullName(userId);
          return { userId, fullName };
        });

        const userNameResults = await Promise.all(userNamePromises);
        const freshUserNames = {};
        userNameResults.forEach(({ userId, fullName }) => {
          freshUserNames[userId] = fullName;
        });

        // Add system users directly to avoid Firebase permissions errors
        uniqueUserIds.forEach(userId => {
          if (userId === 'System' || userId === 'system' || userId === 'N/A' || userId.includes('system@')) {
            freshUserNames[userId] = 'Video Conference System';
          }
        });

        console.log('👥 Fresh user names fetched (including system users):', freshUserNames);

        // Enrich logs with full user names (prioritize existing userFullName for system users)
        const enrichedLogs = logsList.map(log => {
          const userId = log.userId || log.authorId;
          
          // PRIORITY: Use existing userFullName from log data (prevents system user fetch issues)
          let userFullName = log.userFullName || log.userName;
          
          // FALLBACK: Use fetched user names for real users
          if (!userFullName) {
            userFullName = freshUserNames[userId] || 'Unknown User';
          }
          
          // FINAL FALLBACK: System users
          if (!userFullName && (userId === 'System' || userId === 'system' || !userId)) {
            userFullName = 'Video Conference System';
          }
          
          return {
            ...log,
            userFullName: userFullName
          };
        });

        console.log('📋 Final enriched logs count:', enrichedLogs.length);
        
        // DEBUG: Check for video call events specifically
        const videoCallLogs = enrichedLogs.filter(log => log.action?.includes('video_call_'));
        if (videoCallLogs.length > 0) {
          console.log('🎥 VIDEO CALL LOGS FOUND:', videoCallLogs.map(log => ({
            action: log.action,
            userFullName: log.userFullName,
            userId: log.userId,
            timestamp: log.timestamp,
            details: log.details
          })));
        }

        // Apply filter if not 'all'
        const filteredLogs = filter === 'all' 
          ? enrichedLogs 
          : enrichedLogs.filter(log => {
              // Handle filter matching for different activity types
              if (filter === 'incident_created') {
                return log.action?.includes('incident_');
              } else if (filter === 'task_created') {
                return log.action?.includes('task_');
              } else if (filter === 'milestone_created') {
                return log.action?.includes('milestone_');
              } else if (filter === 'staff_status_changed') {
                return (
                  log.action?.includes('staff') || 
                  log.action === 'staff_status_changed' ||
                  log.action === 'staff_status_updated' ||
                  log.action === 'staff_manual_entry' ||
                  log.action === 'staff_removed' ||
                  log.action === 'sms_reply_received' ||
                  log.type?.includes('staff')
                );
              } else if (filter === 'document_uploaded') {
                return log.action?.includes('document_');
              } else if (filter === 'live_chat_message_created') {
                return log.action?.includes('message_') || log.action?.includes('chat_') || log.action?.includes('comms_');
                              } else if (filter === 'video_call_room_created') {
                  return log.action?.includes('video_call_');
              } else {
                return log.action === filter;
              }
            });

        console.log(`📋 Filter applied: "${filter}"`);
        console.log(`📋 Logs before filtering: ${enrichedLogs.length}`);
        console.log(`📋 Logs after filtering: ${filteredLogs.length}`);
        console.log('📋 Sample actions found:', enrichedLogs.map(log => log.action).slice(0, 10));

        setLogs(filteredLogs);
        setUserNames(freshUserNames); // Update cache
        setLoading(false);
      },
      (error) => {
        console.error('Error in logs listener:', error);
        setError('Failed to load logs');
        setLoading(false);
      }
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [incidentId, userOrgId, db, filter]);

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  // PDF Export function for evidenciary purposes
  const exportToPDF = async () => {
    try {
      console.log('🔄 Starting PDF export...');
      toast.info('Generating evidence-grade PDF export...');
      
      // Check if we have logs to export
      if (!logs || logs.length === 0) {
        toast.error('No logs available to export');
        return;
      }
      
      console.log('📊 Exporting', logs.length, 'log entries');
      
      // Get incident details for header
      const incidentDoc = await getDoc(doc(db, 'c3_incidents', incidentId));
      const incidentData = incidentDoc.exists() ? incidentDoc.data() : {};
      
      console.log('📄 Incident data loaded:', incidentData.title || 'No title');
      
      // Create new PDF document in LANDSCAPE orientation for better field visibility
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 20;
      
      console.log('📝 PDF document created in landscape, page width:', pageWidth);
      
      // Test basic PDF functionality
      try {
        pdf.setFontSize(12);
        console.log('✅ Basic PDF operations working');
      } catch (basicError) {
        console.error('❌ Basic PDF operations failed:', basicError);
        throw new Error('jsPDF library not working: ' + basicError.message);
      }
      
      // Check if autoTable is available
      if (typeof pdf.autoTable !== 'function') {
        console.warn('⚠️ autoTable plugin not available, using manual table fallback');
      } else {
        console.log('✅ autoTable plugin available');
      }
      
      // Generate export metadata for chain of custody
      const exportTimestamp = new Date();
      const exporterId = currentUser?.uid || 'Unknown';
      const exporterName = userNames[exporterId] || currentUser?.email || 'Unknown User';
      const filterDescription = logTypes.find(t => t.id === filter)?.name || 'All Activities';
      
      // Calculate content hash for integrity verification
      const logContent = logs.map(log => `${log.timestamp}-${log.action}-${log.details}-${log.userFullName}`).join('|');
      const contentHash = btoa(logContent).substring(0, 16); // Simple hash for integrity
      
      // Header - Crisis Compass Logo Area
      pdf.setFontSize(20);
      pdf.setTextColor(57, 50, 91); // Crisis Compass purple
      pdf.text('CRISIS COMPASS', margin, 25);
      pdf.setFontSize(16);
      pdf.text('INCIDENT ACTIVITY LOG - EVIDENCE EXPORT', margin, 35);
      
      // Export metadata box
      pdf.setDrawColor(57, 50, 91);
      pdf.setLineWidth(0.5);
      pdf.rect(margin, 45, pageWidth - 2 * margin, 35);
      
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      let yPos = 52;
      
      pdf.text(`INCIDENT ID: ${incidentId}`, margin + 5, yPos);
      yPos += 4;
      pdf.text(`INCIDENT TITLE: ${incidentData.title || 'N/A'}`, margin + 5, yPos);
      yPos += 4;
      pdf.text(`INCIDENT STATUS: ${incidentData.status || 'N/A'}`, margin + 5, yPos);
      yPos += 4;
      pdf.text(`INCIDENT SEVERITY: ${incidentData.severity || 'N/A'}`, margin + 5, yPos);
      yPos += 4;
      pdf.text(`LOCATION: ${incidentData.location || 'N/A'}`, margin + 5, yPos);
      
      // Export chain of custody information
      yPos = 52;
      const rightColumn = pageWidth / 2 + 10;
      pdf.text(`EXPORTED BY: ${exporterName}`, rightColumn, yPos);
      yPos += 4;
      pdf.text(`EXPORT DATE: ${exportTimestamp.toLocaleString()}`, rightColumn, yPos);
      yPos += 4;
      pdf.text(`FILTER APPLIED: ${filterDescription}`, rightColumn, yPos);
      yPos += 4;
      pdf.text(`TOTAL RECORDS: ${logs.length}`, rightColumn, yPos);
      yPos += 4;
      pdf.text(`INTEGRITY HASH: ${contentHash}`, rightColumn, yPos);
      
      // Legal disclaimer
      yPos = 90;
      pdf.setFontSize(8);
      pdf.setTextColor(100, 100, 100);
      pdf.text('This document contains a chronological audit trail of incident activities for evidenciary and compliance purposes.', margin, yPos);
      yPos += 3;
      pdf.text('All timestamps are in local system time. This export is immutable and digitally verifiable.', margin, yPos);
      
             // Prepare table data for evidence report (Priority removed for evidence purposes)
       console.log('🗂️ Preparing evidence table data...');
       const tableData = logs.map((log, index) => {
         try {
           const timestamp = log.timestamp?.toDate ? log.timestamp.toDate().toLocaleString() : 
                            log.timestamp ? new Date(log.timestamp).toLocaleString() : 'Unknown time';
           const action = (log.action?.replace(/_/g, ' ').toUpperCase() || 'ACTIVITY');
           const user = log.userFullName || 'System';
           const details = (log.details || 'No details recorded'); // Full details in landscape mode
           
           // NOTE: Priority excluded from evidence report but retained in system logging
           return [timestamp, action, user, details];
         } catch (rowError) {
           console.error('❌ Error processing log row', index, ':', rowError);
           return ['Error', 'Error', 'Error', 'Error processing this log entry'];
         }
       });
      
      console.log('📋 Table data prepared:', tableData.length, 'rows');
      
             // Create table with error handling
       console.log('🏗️ Creating PDF table...');
       
       // Check if autoTable is available and use it, otherwise fallback to manual table
       if (typeof pdf.autoTable === 'function') {
         console.log('📊 Using autoTable for professional formatting...');
         try {
           pdf.autoTable({
             startY: yPos + 10,
             head: [['TIMESTAMP', 'ACTION TYPE', 'PERFORMED BY', 'DETAILS']],
             body: tableData,
                        styles: {
             fontSize: 8,
             cellPadding: 2,
             lineColor: [200, 200, 200],
             lineWidth: 0.1,
             overflow: 'linebreak', // Enable text wrapping for all cells
             cellWidth: 'wrap'
           },
             headStyles: {
               fillColor: [57, 50, 91],
               textColor: [255, 255, 255],
               fontStyle: 'bold'
             },
             alternateRowStyles: {
               fillColor: [248, 249, 250]
             },
             columnStyles: {
               0: { cellWidth: 45, overflow: 'linebreak' }, // Timestamp - wrap if needed
               1: { cellWidth: 40, overflow: 'linebreak' }, // Action - wrap if needed
               2: { cellWidth: 35, overflow: 'linebreak' }, // User - wrap if needed
               3: { cellWidth: 140, overflow: 'linebreak', cellPadding: 3 } // Details - MORE SPACE (was 120mm + 20mm from Priority)
             },
             margin: { left: margin, right: margin },
             didDrawPage: function(data) {
               // Add page numbers and footer on each page
               pdf.setFontSize(8);
               pdf.setTextColor(100, 100, 100);
               const pageCount = pdf.internal.getNumberOfPages();
               const pageNum = pdf.internal.getCurrentPageInfo().pageNumber;
               pdf.text(`Page ${pageNum} of ${pageCount}`, pageWidth - 35, pdf.internal.pageSize.getHeight() - 10);
               pdf.text(`Crisis Compass Evidence Export - ${exportTimestamp.toLocaleDateString()}`, margin, pdf.internal.pageSize.getHeight() - 10);
             }
           });
           console.log('✅ PDF table created successfully with autoTable');
         } catch (tableError) {
           console.error('❌ Error creating PDF table with autoTable:', tableError);
           throw new Error('Failed to create PDF table: ' + tableError.message);
         }
       } else {
         console.log('📝 Using manual table fallback...');
         try {
           // Manual table creation without autoTable with text wrapping
           let currentY = yPos + 20;
           const baseRowHeight = 6;
           const headerHeight = 8;
           
           // Text wrapping helper function
           const wrapText = (text, maxWidth, fontSize = 8) => {
             if (!text) return [''];
             
             pdf.setFontSize(fontSize);
             const words = text.split(' ');
             const lines = [];
             let currentLine = '';
             
             for (const word of words) {
               const testLine = currentLine + (currentLine ? ' ' : '') + word;
               const testWidth = pdf.getTextWidth(testLine);
               
               if (testWidth > maxWidth && currentLine) {
                 lines.push(currentLine);
                 currentLine = word;
               } else {
                 currentLine = testLine;
               }
             }
             
             if (currentLine) {
               lines.push(currentLine);
             }
             
             return lines.length > 0 ? lines : [''];
           };
           
           // Draw table header
           pdf.setFontSize(8);
           pdf.setFont(undefined, 'bold');
           pdf.setFillColor(57, 50, 91);
           pdf.setTextColor(255, 255, 255);
           pdf.rect(margin, currentY, pageWidth - 2 * margin, headerHeight, 'F');
           
                                  // Header text - better spacing for landscape (Priority removed for evidence report)
           pdf.text('TIMESTAMP', margin + 2, currentY + 5);
           pdf.text('ACTION TYPE', margin + 47, currentY + 5);
           pdf.text('PERFORMED BY', margin + 87, currentY + 5);
           pdf.text('DETAILS', margin + 122, currentY + 5);
           
           currentY += headerHeight;
           
           // Draw table rows
           pdf.setFont(undefined, 'normal');
           pdf.setTextColor(0, 0, 0);
           
                      tableData.forEach((row, index) => {
             // Wrap text for each column with appropriate widths (Priority removed for evidence report)
             const timestampLines = wrapText(row[0] || '', 40); // ~45mm column
             const actionLines = wrapText(row[1] || '', 35);     // ~40mm column  
             const userLines = wrapText(row[2] || '', 30);       // ~35mm column
             const detailsLines = wrapText(row[3] || '', 130);   // ~140mm column - MUCH MORE SPACE for critical details!
             
             // Calculate row height based on maximum lines needed (Priority removed)
             const maxLines = Math.max(
               timestampLines.length,
               actionLines.length, 
               userLines.length,
               detailsLines.length
             );
             const rowHeight = Math.max(baseRowHeight, maxLines * 4 + 2); // 4mm per line + padding
             
             // Alternate row colors
             if (index % 2 === 0) {
               pdf.setFillColor(248, 249, 250);
               pdf.rect(margin, currentY, pageWidth - 2 * margin, rowHeight, 'F');
             }
             
             // Draw wrapped text for each column
             pdf.setFontSize(8);
             pdf.setTextColor(0, 0, 0);
             
             // Helper to draw multiline text
             const drawMultilineText = (lines, x, startY) => {
               lines.forEach((line, lineIndex) => {
                 pdf.text(line, x, startY + (lineIndex * 4));
               });
             };
             
             // Draw all columns with proper wrapping (Priority removed for evidence report)
             drawMultilineText(timestampLines, margin + 2, currentY + 4);
             drawMultilineText(actionLines, margin + 47, currentY + 4);
             drawMultilineText(userLines, margin + 87, currentY + 4);
             drawMultilineText(detailsLines, margin + 122, currentY + 4); // Critical details column - NOW WITH MORE SPACE!
             
             currentY += rowHeight;
             
             // Page break if needed
             if (currentY > pdf.internal.pageSize.getHeight() - 30) {
               pdf.addPage();
               currentY = 20;
             }
           });
           
           console.log('✅ Manual table created successfully');
         } catch (manualError) {
           console.error('❌ Error creating manual table:', manualError);
           throw new Error('Failed to create manual table: ' + manualError.message);
         }
       }
      
      // Add digital signature page
      pdf.addPage();
      pdf.setFontSize(14);
      pdf.setTextColor(57, 50, 91);
      pdf.text('DIGITAL VERIFICATION & CHAIN OF CUSTODY', margin, 30);
      
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      let signatureY = 50;
      
      pdf.text('EXPORT CERTIFICATION:', margin, signatureY);
      signatureY += 8;
      pdf.text(`• This document was generated automatically by Crisis Compass on ${exportTimestamp.toLocaleString()}`, margin + 5, signatureY);
      signatureY += 5;
      pdf.text(`• Exported by: ${exporterName} (${currentUser?.email || 'Unknown'})`, margin + 5, signatureY);
      signatureY += 5;
      pdf.text(`• Source Incident ID: ${incidentId}`, margin + 5, signatureY);
      signatureY += 5;
      pdf.text(`• Records Included: ${logs.length} activity log entries`, margin + 5, signatureY);
      signatureY += 5;
      pdf.text(`• Filter Applied: ${filterDescription}`, margin + 5, signatureY);
      signatureY += 5;
      pdf.text(`• Content Integrity Hash: ${contentHash}`, margin + 5, signatureY);
      
      signatureY += 15;
      pdf.text('LEGAL NOTICE:', margin, signatureY);
      signatureY += 8;
      pdf.text('• This document contains an immutable audit trail for evidenciary purposes', margin + 5, signatureY);
      signatureY += 5;
      pdf.text('• All log entries are sourced from authenticated Crisis Compass database records', margin + 5, signatureY);
      signatureY += 5;
      pdf.text('• Timestamps reflect actual system time when activities occurred', margin + 5, signatureY);
      signatureY += 5;
      pdf.text('• Any modification to this document invalidates its evidenciary value', margin + 5, signatureY);
      signatureY += 5;
      pdf.text('• This export complies with digital evidence standards for incident management', margin + 5, signatureY);
      
      signatureY += 15;
      pdf.text('VERIFICATION DETAILS:', margin, signatureY);
      signatureY += 8;
      pdf.text(`• Export Timestamp: ${exportTimestamp.toISOString()}`, margin + 5, signatureY);
      signatureY += 5;
      pdf.text(`• System User ID: ${exporterId}`, margin + 5, signatureY);
      signatureY += 5;
      pdf.text(`• Organization: ${incidentData.orgId || 'Unknown'}`, margin + 5, signatureY);
      signatureY += 5;
      pdf.text(`• Platform Version: Crisis Compass v3.0`, margin + 5, signatureY);
      
      // Generate filename with timestamp and filter
      const timestamp = exportTimestamp.toISOString().replace(/[:.]/g, '-').split('T')[0];
      const filterSuffix = filter === 'all' ? 'AllActivities' : filter.replace(/_/g, '');
      const filename = `CrisisCompass_IncidentLog_${incidentId}_${filterSuffix}_${timestamp}.pdf`;
      
      console.log('💾 Saving PDF with filename:', filename);
      
      // Save the PDF
      try {
        pdf.save(filename);
        console.log('✅ PDF saved successfully');
        toast.success(`Evidence-grade PDF exported: ${filename}`);
      } catch (saveError) {
        console.error('❌ Error saving PDF:', saveError);
        throw new Error('Failed to save PDF: ' + saveError.message);
      }
      
    } catch (error) {
      console.error('❌ PDF Export Error:', error);
      console.error('❌ Error details:', error.message, error.stack);
      toast.error(`Failed to export PDF: ${error.message || 'Please try again'}`);
    }
  };

  const getLogIcon = (action) => {
    // Return empty string for professional display without icons
    return '';
  };

  const getLogColor = (action) => {
    switch (action) {
      // Incident events - blue theme
      case 'incident_created':
      case 'incident_updated':
      case 'incident_status_changed':
      case 'incident_severity_changed':
      case 'incident_location_changed':
      case 'incident_occurrence_time_changed':
        return 'text-primary';
      case 'incident_deleted':
        return 'text-danger';
      
      // Task events - green theme
      case 'task_created':
      case 'task_updated':
      case 'task_status_changed':
      case 'task_assigned':
        return 'text-success';
      case 'task_deleted':
        return 'text-danger';
      
      // Milestone events - purple theme
      case 'milestone_created':
      case 'milestone_updated':
      case 'milestone_status_changed':
      case 'milestone_moved':
        return 'text-info';
      case 'milestone_deleted':
        return 'text-danger';
      
      // Regulatory events - orange theme
      case 'regulatory_triggers_updated':
      case 'regulatory_task_created':
        return 'text-warning';
      
      // Staff events
      case 'staff_status_changed':
        return 'text-primary';
      case 'staff_status_updated':
        return 'text-success';
      case 'staff_manual_entry':
        return 'text-danger';
      case 'staff_removed':
        return 'text-warning';
      case 'staff_location_updated':
        return 'text-info';
      case 'sms_reply_received':
        return 'text-success';
      
      // Legacy and other events
      case 'task':
        return 'text-success';
      case 'note':
        return 'text-info';
      case 'message':
        return 'text-warning';
      case 'system':
        return 'text-secondary';
      default:
        return 'text-dark';
    }
  };

  // Function to get user's full name from user document
  const getUserFullName = async (userId) => {
    if (!userId || userId === 'System' || userId === 'N/A') {
      return 'System';
    }

    try {
      const userDocRef = doc(db, 'c3_users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Priority 1: Check for fullName field
        if (userData.fullName) {
          return userData.fullName;
        }
        
        // Priority 2: Try to construct from first/last name
        const firstName = userData.firstName || userData.first_name || userData.name?.first || '';
        const lastName = userData.lastName || userData.last_name || userData.name?.last || '';
        
        if (firstName && lastName) {
          const fullName = `${firstName} ${lastName}`;
          return fullName;
        } else if (firstName) {
          return firstName;
        } else if (lastName) {
          return lastName;
        }
        
        // Priority 3: Fallback to email-derived name
        if (userData.email) {
          const emailName = userData.email.split('@')[0].replace(/[._]/g, ' ');
          return emailName;
        }

        return 'Unknown User';
      } else {
        console.warn(`❌ User document not found for userId: ${userId}`);
        return 'Unknown User';
      }
    } catch (error) {
      console.error(`❌ Error fetching user data for ${userId}:`, error);
      return 'Unknown User';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading logs...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <div className="d-flex justify-content-between align-items-center">
          <span>{error}</span>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={() => setError(null)}
            type="primary"
            style={{ backgroundColor: '#39325b', borderColor: '#39325b' }}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="incident-log-container">
      <style>{`
        /* MIRROR SUCCESSFUL MAIN TABS RESPONSIVE APPROACH */
        .responsive-filter-container {
          overflow-x: auto;
          flex: 1;
          margin-right: 16px;
        }
        
        .responsive-filter-container .btn-group {
          white-space: nowrap;
          min-width: fit-content;
        }
        
        .responsive-filter-container .btn {
          /* Default - mirror main tabs sizing */
          min-width: 120px !important;
          padding: 5px 11px !important;
          margin: 0 2px !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          white-space: nowrap !important;
          border-radius: 4px !important;
        }
        
        /* 1200px breakpoint - mirror main tabs exactly */
        @media (max-width: 1200px) {
          .responsive-filter-container .btn {
            min-width: 100px !important;
            padding: 4px 9px !important;
            margin: 0 1px !important;
          }
        }
        
        /* 768px breakpoint - mirror main tabs exactly */
        @media (max-width: 768px) {
          .responsive-filter-container .btn {
            min-width: 80px !important;
            padding: 3px 7px !important;
            margin: 0 1px !important;
            font-size: 11px !important;
          }
        }
        
        /* Mobile text truncation for long button labels */
        @media (max-width: 768px) {
          .responsive-filter-container .btn {
            text-overflow: ellipsis !important;
            overflow: hidden !important;
          }
        }
      `}</style>
      <div className="row mb-4">
        <div className="col">
          <div className="d-flex align-items-center">
            <div className="responsive-filter-container">
          <div className="btn-group" role="group">
            {logTypes.map(type => (
              <Tooltip key={type.id} title={type.tooltip} placement="top">
                <button
                  type="button"
                  className={`btn ${filter === type.id ? 'btn-primary' : 'btn-outline-primary'}`}
                  style={filter === type.id ? { backgroundColor: '#39325b', borderColor: '#39325b' } : {}}
                  onClick={() => handleFilterChange(type.id)}
                    data-filter={type.id}
                >
                  {type.name}
                </button>
              </Tooltip>
            ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="timeline">
        {logs.length === 0 ? (
          <Empty
            description={filter === 'all' ? 
              "No incident logs found yet. Logs will appear here as crisis team members take actions during this incident." :
              `No ${logTypes.find(t => t.id === filter)?.name.toLowerCase()} logs found for this incident.`
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          logs.map(log => (
            <div 
              key={log.id} 
              className={`timeline-item ${getLogColor(log.action)}`}
              style={{
                marginBottom: '16px',
                padding: '16px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                borderLeft: '4px solid #39325b'
              }}
            >
              <div className="timeline-icon">
                {getLogIcon(log.action)}
              </div>
              <div className="timeline-content">
                <div className="timeline-header" style={{ marginBottom: '8px' }}>
                  <span className="timeline-time" style={{ fontSize: '14px', color: '#6c757d' }}>
                    {log.timestamp?.toDate ? log.timestamp.toDate().toLocaleString() : 
                     new Date(log.timestamp).toLocaleString()}
                  </span>
                  <span className="timeline-type" style={{ 
                    fontSize: '12px', 
                    fontWeight: 'bold', 
                    color: '#39325b',
                    marginLeft: '12px',
                    backgroundColor: '#e9ecef',
                    padding: '2px 8px',
                    borderRadius: '4px'
                  }}>
                    {log.action?.replace(/_/g, ' ').toUpperCase() || 'ACTIVITY'}
                  </span>
                  {log.priority && (
                    <span 
                      className={`timeline-priority priority-${log.priority}`}
                      style={{
                        fontSize: '11px',
                        fontWeight: 'bold',
                        marginLeft: '8px',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        backgroundColor: log.priority === 'high' ? '#dc3545' : log.priority === 'critical' ? '#dc3545' : '#28a745',
                        color: 'white'
                      }}
                    >
                      {log.priority.toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="timeline-body" style={{ 
                  fontSize: '16px', 
                  lineHeight: '1.4',
                  marginBottom: '8px',
                  color: '#212529'
                }}>
                  {log.details}
                </div>
                <div className="timeline-footer">
                  <small style={{ color: '#6c757d', fontSize: '13px' }}>
                    <strong>By: {log.userFullName || 'System'}</strong>
                  </small>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      
      {/* PDF Export Button - Bottom Left */}
      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-start' }}>
        <Tooltip 
          title="Export current logs as immutable PDF for evidence purposes. Includes digital verification and chain of custody information."
          placement="top"
        >
          <Button
            icon={<DownloadOutlined />}
            onClick={exportToPDF}
            disabled={logs.length === 0}
            size="small"
            style={{
              backgroundColor: '#39325b',
              borderColor: '#39325b',
              color: 'white',
              fontSize: '12px',
              transform: 'scale(1.5)',
              transformOrigin: 'left center'
            }}
                        >
                PDF
              </Button>
        </Tooltip>
      </div>
    </div>
  );
};

export default IncidentLogTab; 